import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Volume2, ShieldCheck, Thermometer, Award } from "lucide-react";
import { MaskedLine, Reveal, Eyebrow } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { IMAGES, PRODUCTS, GALLERY } from "../data/products";

const STATS = [
  { value: "Up to 49 dB", label: "Noise Reduction" },
  { value: "0.92 W/m²K", label: "Thermal U-Value" },
  { value: "500+", label: "Installations" },
  { value: "20 Yr", label: "Profile Warranty" },
];

const CHAPTERS = [
  { num: "01", title: "Engineering Precision", text: "Every frame is CNC-cut to a tenth of a millimetre, fusion-welded at the corners, and reinforced with galvanized steel — so it never warps, rattles, or leaks." },
  { num: "02", title: "German-Grade Profiles", text: "We fabricate from UV-stabilized, lead-free uPVC profiles rated for tropical solar load — the same polymer science trusted across Europe for four decades." },
  { num: "03", title: "Tropical-Tested", text: "Monsoon wind pressure, 45°C summers, coastal salt air. Our systems are tested against Indian weather classes, not European brochures." },
  { num: "04", title: "Architectural Aesthetics", text: "Slim sightlines, foil finishes from Anthracite to Mahogany, and hardware that disappears into the frame. Performance should look this good." },
];

const REVIEWS = [
  { type: "rating", source: "Google Reviews", score: "4.6", detail: "Verified Google Business rating", link: "https://maps.app.goo.gl/XzTKDAD9kYmswv4a8" },
  { quote: "Amazing place for modern-day needs. Look and feel are of European standards — best suited for people looking for a long-term, luxury home.", name: "Dhirendra Kumar Panda", role: "Google Review" },
  { quote: "Good quality uPVC windows. A nice display centre to view windows and doors of different models.", name: "Kishan Patel", role: "Google Review" },
  { quote: "Great quality windows, excellent and knowledgeable staff. Would definitely recommend to others.", name: "Anmol Patel", role: "Google Review" },
  { quote: "Best quality uPVC doors and windows, and great service provided.", name: "Niddhi Pathak", role: "Google Review" },
  { quote: "New-gen doors and windows — the best solution for doors and windows.", name: "Jagan Sri", role: "Google Review" },
  { quote: "Service is excellent. The company is highly focused on customer satisfaction.", name: "Jagadeesh Burla", role: "Google Review" },
  { type: "rating", source: "Google Rating · Akash Goud", score: "5.0", detail: "Rated on Google", link: "https://maps.app.goo.gl/XzTKDAD9kYmswv4a8" },
  { type: "rating", source: "Google Rating · Rajesh Joshi", score: "4.8", detail: "Rated on Google", link: "https://maps.app.goo.gl/XzTKDAD9kYmswv4a8" },
  { type: "rating", source: "Justdial", score: "4.9", detail: "45 verified ratings", link: "https://maps.app.goo.gl/XzTKDAD9kYmswv4a8" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="grain-overlay relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src="/images/home-hero.webp" alt="SOHOM sliding doors opening to a terrace view" className="h-[120%] w-full scale-105 object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-black/60 to-black/45" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(0,160,227,0.12),transparent_55%)]" />

      <motion.div className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-32 pb-20 sm:px-10" style={{ opacity: fade }}>
        <Eyebrow className="mb-6 !text-sky-300">Windows &amp; Doors — Fabricated In-House</Eyebrow>
        <h1 className="font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl" data-testid="hero-headline">
          <MaskedLine delay={0.15}>Framing Light.</MaskedLine>
          <MaskedLine delay={0.32} className="text-gold">Sealing Silence.</MaskedLine>
          <MaskedLine delay={0.49}>Crafting Comfort.</MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          We design, fabricate and install precision-engineered uPVC windows and doors in Hyderabad that cut noise by up to 49 dB, slash heat gain, and outlast the building itself.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/products" data-testid="hero-explore-products" className="group flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 font-semibold text-obsidian transition-colors duration-300 hover:bg-gold-hover">
            Explore Systems
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link to="/contact" data-testid="hero-quote-cta" className="flex items-center gap-2 rounded-full border border-slate-600 px-7 py-3.5 font-semibold text-slate-200 transition-all duration-300 hover:border-gold hover:text-gold">
            Get a Free Quote
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-16 grid max-w-2xl grid-cols-2 gap-6 border-t border-slate-700/60 pt-8 sm:grid-cols-4"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-heading text-2xl font-bold text-gold sm:text-3xl">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

const ProductBento = () => {
  const picks = [PRODUCTS[0], PRODUCTS[1], PRODUCTS[2], PRODUCTS[4]];
  return (
    <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-32" data-testid="home-product-highlights">
      <Reveal>
        <Eyebrow>Signature Systems</Eyebrow>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
          <h2 className="max-w-xl font-heading text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Four systems. One obsession with the perfect opening.
          </h2>
          <Link to="/products" className="group flex items-center gap-2 text-sm font-semibold text-gold" data-testid="highlights-view-all">
            View full catalogue
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-12">
        {picks.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1} className={i % 3 === 0 ? "md:col-span-7" : "md:col-span-5"}>
            <Link
              to="/products"
              className="group relative block h-80 overflow-hidden rounded-xl border border-sky-400/20 bg-surface shadow-2xl transition-shadow duration-500 hover:shadow-[0_0_60px_-15px_rgba(0,160,227,0.35)] md:h-96"
              data-testid={`highlight-card-${p.id}`}
            >
              <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{p.badge}</p>
                <h3 className="mt-2 font-heading text-xl font-bold text-slate-50 sm:text-2xl">{p.name}</h3>
                <p className="mt-1 text-sm italic text-slate-400">{p.tagline}</p>
              </div>
              <span className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full border border-slate-500/40 bg-black/40 opacity-0 backdrop-blur-md transition-all duration-500 group-hover:opacity-100">
                <ArrowUpRight className="h-4 w-4 text-gold" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Manifesto = () => (
  <section className="border-y border-border bg-surface/40" data-testid="home-manifesto">
    <div className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-32">
      <Reveal>
        <Eyebrow>The SOHOM Standard</Eyebrow>
        <h2 className="mt-4 max-w-2xl font-heading text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
          A manifesto, welded into every corner.
        </h2>
      </Reveal>
      <div className="mt-16">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.num} delay={0.05 * i}>
            <div
              className={`group flex flex-col gap-4 border-t border-border py-10 transition-colors duration-500 hover:bg-gold/[0.03] sm:flex-row sm:items-baseline sm:gap-12 ${
                i === CHAPTERS.length - 1 ? "border-b" : ""
              }`}
              data-testid={`manifesto-chapter-${c.num}`}
            >
              <span className="text-stroke-gold font-heading text-6xl font-extrabold leading-none sm:text-7xl">{c.num}</span>
              <h3 className="font-heading text-xl font-semibold text-slate-100 sm:w-64 sm:shrink-0 sm:text-2xl">{c.title}</h3>
              <p className="max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{c.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const AcousticLab = () => {
  const [outside, setOutside] = useState(85);
  const inside = Math.max(25, outside - 49);
  return (
    <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-32" data-testid="home-acoustic-lab">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>Interactive — The Silence Lab</Eyebrow>
          <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Drag the city up. Watch your room go quiet.
          </h2>
          <p className="mt-6 max-w-lg text-sm leading-relaxed text-slate-400 sm:text-base">
            Traffic, horns, construction — Indian streets routinely cross 85 dB. Our double-glazed, multi-chamber uPVC windows and doors cut up to 49 dB, turning a jet-loud street into a whisper-soft interior.
          </p>
          <div className="mt-10">
            <div className="flex items-center justify-between font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
              <span>Quiet Street</span>
              <span>Rush Hour</span>
            </div>
            <input
              type="range"
              min="60"
              max="100"
              value={outside}
              onChange={(e) => setOutside(Number(e.target.value))}
              className="mt-4 w-full accent-gold"
              data-testid="acoustic-slider"
            />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="rounded-xl border border-border bg-carddark p-8" data-testid="outside-noise-card">
              <Volume2 className="h-6 w-6 text-red-400" />
              <p className="mt-6 font-heading text-5xl font-extrabold text-slate-100">{outside}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">dB Outside</p>
              <p className="mt-4 text-sm text-slate-400">{outside >= 85 ? "Busy arterial road" : outside >= 72 ? "Typical urban street" : "Residential lane"}</p>
            </div>
            <div className="rounded-xl border border-gold/30 bg-gold/[0.06] p-8" data-testid="inside-noise-card">
              <ShieldCheck className="h-6 w-6 text-gold" />
              <p className="mt-6 font-heading text-5xl font-extrabold text-gold">{inside}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold/70">dB Inside</p>
              <p className="mt-4 text-sm text-slate-300">{inside <= 30 ? "Library quiet" : inside <= 40 ? "Calm conversation level" : "Soft background hum"}</p>
            </div>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-3 rounded-lg border border-border bg-surface/60 px-5 py-4">
              <Thermometer className="h-5 w-5 text-gold" />
              <p className="text-sm text-slate-300">Up to <span className="font-semibold text-slate-100">30% lower</span> cooling bills</p>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-border bg-surface/60 px-5 py-4">
              <Award className="h-5 w-5 text-gold" />
              <p className="text-sm text-slate-300"><span className="font-semibold text-slate-100">20-year</span> profile warranty</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const GalleryPreview = () => (
  <section className="border-y border-border bg-surface/40" data-testid="home-gallery-preview">
    <div className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-32">
      <Reveal>
        <Eyebrow>Installed, Not Promised</Eyebrow>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
          <h2 className="max-w-xl font-heading text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Recent uPVC installations across Hyderabad, Bengaluru &amp; Goa.
          </h2>
          <Link to="/gallery" className="group flex items-center gap-2 text-sm font-semibold text-gold" data-testid="gallery-preview-link">
            Open gallery
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </Reveal>
      <div className="mt-14 grid gap-6 sm:grid-cols-3">
        {[GALLERY[3], GALLERY[0], GALLERY[4]].map((g, i) => (
          <Reveal key={g.id} delay={i * 0.12}>
            <Link to="/gallery" className="group block overflow-hidden rounded-xl border border-sky-400/20" data-testid={`gallery-preview-${g.id}`}>
              <div className="h-72 overflow-hidden">
                <img src={g.image} alt={g.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="bg-carddark p-5">
                <p className="font-heading font-semibold text-slate-100">{g.title}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{g.location}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="overflow-hidden py-20 lg:py-32" data-testid="home-testimonials">
    <div className="mx-auto max-w-7xl px-6 sm:px-10">
      <Reveal>
        <Eyebrow>Word From the Field</Eyebrow>
        <h2 className="mt-4 max-w-xl font-heading text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
          Rated 4.6 on Google. Loved across Hyderabad.
        </h2>
      </Reveal>
    </div>
    <Reveal delay={0.1}>
      <div className="relative mt-14">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-obsidian to-transparent sm:w-28" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-obsidian to-transparent sm:w-28" />
        <div className="marquee-track flex w-max gap-6" style={{ animationDuration: "70s" }} data-testid="reviews-slider">
          {[...REVIEWS, ...REVIEWS].map((r, i) =>
            r.type === "rating" ? (
              <a
                key={i}
                href={r.link}
                target="_blank"
                rel="noreferrer"
                className="flex w-[280px] shrink-0 flex-col justify-center rounded-xl border border-gold/30 bg-gold/[0.06] p-8 transition-colors duration-300 hover:border-gold/60"
                data-testid={`review-card-${i}`}
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/70">{r.source}</p>
                <p className="mt-3 font-heading text-6xl font-extrabold text-gold">{r.score}</p>
                <p className="mt-2 text-sm text-slate-400">{r.detail}</p>
              </a>
            ) : (
              <figure key={i} className="flex w-[340px] shrink-0 flex-col rounded-xl border border-border bg-carddark p-8" data-testid={`review-card-${i}`}>
                <span className="font-heading text-5xl leading-none text-gold">"</span>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-300">{r.quote}</blockquote>
                <figcaption className="mt-8 border-t border-border pt-5">
                  <p className="font-heading font-semibold text-slate-100">{r.name}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{r.role}</p>
                </figcaption>
              </figure>
            )
          )}
        </div>
      </div>
    </Reveal>
  </section>
);

const CTABanner = () => (
  <section className="relative overflow-hidden border-t border-border" data-testid="home-cta-banner">
    <img src="/images/cta-banner.webp" alt="SOHOM monorail sliding door track detail" className="absolute inset-0 h-full w-full object-cover opacity-25" />
    <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/85 to-obsidian/60" />
    <div className="relative mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32">
      <Reveal>
        <h2 className="max-w-3xl font-heading text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
          Your project deserves a <span className="text-gold">quieter, cooler</span> frame.
        </h2>
        <p className="mt-6 max-w-xl text-base text-slate-300">
          Share your drawings or a rough sketch — our Hyderabad team will revert with system recommendations and a transparent quote within 24 hours.
        </p>
        <Link to="/contact" data-testid="cta-banner-button" className="group mt-10 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-semibold text-obsidian transition-colors duration-300 hover:bg-gold-hover">
          Book a Free Consultation
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <ProductBento />
      <Manifesto />
      <AcousticLab />
      <GalleryPreview />
      <Testimonials />
      <CTABanner />
    </div>
  );
}
